import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Tick } from "@/components/Annotation";
import { PageMeta } from "@/components/PageMeta";
import { TidyCalEmbedWidget } from "@/components/booking/TidyCalEmbedWidget";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";
import { captureBookingPageViewed, trackAssetViewed } from "@/lib/analytics";

const META_KEYS = {
  presentation: ["meta.book_presentation_title", "meta.book_presentation_description", ROUTES.bookPresentation],
  executive_presence: ["meta.book_executive_presence_title", "meta.book_executive_presence_description", ROUTES.bookExecutivePresence],
  corporate: ["meta.book_corporate_title", "meta.book_corporate_description", ROUTES.bookCorporate],
  ld_partnership: ["meta.book_ld_partnership_title", "meta.book_ld_partnership_description", ROUTES.bookLdPartnership],
};

const ASSET_ID_BY_SERVICE = {
  presentation: "booking_presentation",
  executive_presence: "booking_executive_presence",
  corporate: "booking_corporate",
  ld_partnership: "booking_ld_partnership",
};

/**
 * Single reusable shell for the four dedicated booking pages — content
 * and TidyCal booking type are service-specific, layout/behaviour is
 * shared. Desktop: two columns (intro + "what we'll cover" left, calendar
 * right, near the top). Mobile: eyebrow → title → description → practical
 * → calendar → reassurance → "what we'll cover" → FAQ (calendar is never
 * pushed down by a long explanation on mobile).
 */
export const DedicatedBookingPage = ({ service }) => {
  const { t, l } = useLang();
  const viewedRef = useRef(false);
  // Bug fix (2026-10-12) — `trackAssetViewed()` already consumes the
  // fresh CTA-navigation stash (cta_id/content_id/target_asset_id) at
  // mount and RETURNS it, but this page previously discarded that
  // return value. Held in state (not a plain ref) so the update
  // re-renders and re-passes the real value down to
  // `TidyCalEmbedWidget` before any real `bookingComplete` message
  // could possibly arrive (that requires the visitor to actually
  // finish picking a slot in the embedded calendar).
  const [ctaNavContext, setCtaNavContext] = useState(null);
  const [titleKey, descriptionKey, path] = META_KEYS[service];

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    // Bug fix (2026-10-12) — consume the one-shot CTA stash via
    // `trackAssetViewed()` ONCE, then hand the SAME result to
    // `captureBookingPageViewed()` explicitly (instead of letting it
    // also call `consumeFreshCtaSource()` and find nothing left).
    const navContext = trackAssetViewed(ASSET_ID_BY_SERVICE[service], { placement: "full_page" });
    captureBookingPageViewed(service, navContext);
    setCtaNavContext(navContext);
  }, [service]);

  const base = `book.${service}`;
  const coverItems = t(`${base}.cover_items`);
  const faq = t(`${base}.faq`);

  return (
    <>
      <PageMeta titleKey={titleKey} descriptionKey={descriptionKey} path={path} />

      <section className="relative z-10 pt-10 md:pt-14 pb-6" data-testid="book-page-all-options">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <Link
            to={l(ROUTES.booking)}
            data-testid="book-all-options-link"
            className="inline-flex items-center gap-2 font-body text-sm text-ink-soft hover:text-terracotta transition-colors"
          >
            <span aria-hidden="true">&larr;</span> {t("book.shared.all_options_label")}
          </Link>
        </div>
      </section>

      <section className="relative z-10 pb-24 md:pb-32" data-testid={`book-${service}-hero`}>
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.28em] text-terracotta mb-5 flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-terracotta" />
              {t(`${base}.eyebrow`)}
            </p>
            <h1 className="font-serif text-[2.1rem] sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink max-w-2xl" data-testid={`book-${service}-h1`}>
              {t(`${base}.h1`)}
            </h1>
            <p className="mt-6 font-body text-base md:text-lg text-ink-soft leading-relaxed max-w-xl" data-testid={`book-${service}-description`}>
              {t(`${base}.description`)}
            </p>
            <p className="mt-5 font-hand text-terracotta text-lg md:text-xl" data-testid={`book-${service}-practical`}>
              {t(`${base}.practical`)}
            </p>
            <p className="mt-1 font-body text-sm text-ink-soft" data-testid={`book-${service}-language-line`}>
              {t(`${base}.language_line`)}
            </p>
          </Reveal>

          <div className="mt-12 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left column: what we'll cover (desktop order) */}
            <div className="hidden lg:block lg:col-span-5 lg:order-1" data-testid={`book-${service}-cover-desktop`}>
              <WhatWeCover t={t} base={base} items={coverItems} />
            </div>

            {/* Right column: calendar (desktop), appears first on mobile */}
            <div className="lg:col-span-7 lg:order-2" data-testid="book-calendar-column">
              <Reveal delay={0.1}>
                <h2 className="font-serif text-xl md:text-2xl text-ink mb-2" data-testid={`book-${service}-calendar-title`}>
                  {t(`${base}.calendar_title`)}
                </h2>
                <p className="font-body text-sm text-ink-soft mb-5">{t("book.shared.calendar_microcopy")}</p>
                <TidyCalEmbedWidget service={service} testId={`book-${service}-tidycal`} ctaNavContext={ctaNavContext} />
              </Reveal>

              {/* Reassurance — right under the calendar on every breakpoint */}
              <div className="mt-8 flex flex-col gap-3" data-testid={`book-${service}-reassurance`}>
                <p className="font-body text-ink-soft leading-relaxed flex items-start gap-3">
                  <Tick className="w-5 h-5 mt-0.5 shrink-0" />
                  <span>{t(`${base}.reassurance_1`)}</span>
                </p>
                <p className="font-body text-ink-soft leading-relaxed flex items-start gap-3">
                  <Tick className="w-5 h-5 mt-0.5 shrink-0" />
                  <span>{t(`${base}.reassurance_2`)}</span>
                </p>
              </div>
            </div>

            {/* Mobile-only: what we'll cover, after reassurance */}
            <div className="lg:hidden lg:col-span-5 lg:order-3" data-testid={`book-${service}-cover-mobile`}>
              <WhatWeCover t={t} base={base} items={coverItems} />
            </div>
          </div>

          {/* FAQ */}
          {Array.isArray(faq) && faq.length > 0 && (
            <Reveal delay={0.15} className="mt-16 md:mt-20 max-w-2xl">
              <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-6">FAQ</p>
              <div className="flex flex-col gap-6" data-testid={`book-${service}-faq`}>
                {faq.map((item, i) => (
                  <div key={i} className="border-t border-ink/10 pt-5">
                    <h3 className="font-serif text-lg text-ink mb-2">{item.q}</h3>
                    <p className="font-body text-ink-soft leading-relaxed">{item.a}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
};

const WhatWeCover = ({ t, base, items }) => (
  <Reveal className="mt-10 lg:mt-0">
    <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-6">{t("book.shared.cover_heading")}</p>
    <div className="flex flex-col gap-6">
      {Array.isArray(items) &&
        items.map((item, i) => (
          <div key={i} className="flex gap-4">
            <span className="font-hand text-terracotta text-xl shrink-0 w-6">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <h3 className="font-serif text-lg text-ink mb-1">{item.title}</h3>
              <p className="font-body text-sm text-ink-soft leading-relaxed">{item.body}</p>
            </div>
          </div>
        ))}
    </div>
  </Reveal>
);

export default DedicatedBookingPage;
